<template>
    <div class="student-portal-certificates" :class="{ dark }">
        <div class="student-portal-certificates__inner">
            <header class="certs-hero">
                <div class="certs-hero__copy">
                    <div class="certs-hero__title-row">
                        <h1>{{ $t('student.certificates.myTitle') }}</h1>
                        <button
                            type="button"
                            class="certs-hero__refresh"
                            :disabled="isLoading"
                            @click="loadCertificates"
                        >
                            <v-icon size="16" aria-hidden="true">mdi-refresh</v-icon>
                            {{ $t('common.update') }}
                        </button>
                    </div>
                    <p class="certs-hero__lead">{{ $t('student.certificates.subtitle') }}</p>
                </div>
                <CertificateSummary
                    :total="summary.total"
                    :issued="summary.issued"
                    :pending="summary.pending"
                />
            </header>

            <section class="certs-panel">
                <CertificateFilters
                    :query="filters.query"
                    :period="filters.period"
                    :status="filters.status"
                    :period-options="localizedPeriodFilterOptions"
                    :status-options="statusFilterOptions"
                    :has-active-filters="hasActiveFilters"
                    @update:query="filters.query = $event"
                    @update:period="filters.period = $event"
                    @update:status="filters.status = $event"
                    @clear="clearFilters"
                />

                <CertificateSkeleton v-if="isLoading && !hasReady" />

                <CertificateEmptyState
                    v-else-if="noCertificateModel"
                    :title="$t('student.certificates.noneRegistered')"
                />

                <CertificateEmptyState
                    v-else-if="loadFailed && !dataCertificates.length"
                    error
                    :title="$t('student.certificates.toastLoadError')"
                    @retry="loadCertificates"
                />

                <CertificateEmptyState
                    v-else-if="!filteredCertificates.length"
                    :title="hasActiveFilters ? $t('student.certificates.emptyFiltered') : $t('student.certificates.empty')"
                    :hint="hasActiveFilters ? $t('student.certificates.emptyFilteredHint') : $t('student.certificates.emptyHint')"
                    :show-clear="hasActiveFilters"
                    @clear="clearFilters"
                />

                <ul v-else class="certs-list">
                    <li v-for="item in pagedCertificates" :key="certificateCourseId(item)">
                        <CertificateCard
                            :name="item.courseName"
                            :image="item.courseImagePathFinal || ''"
                            :duration="formatDuration(item.lessonTime)"
                            :issued="isCertificateIssued(item)"
                            :eligible="isCertificateEligible(item)"
                            :date-text="statusDate(item)"
                            :pending-labels="pendingLabels(item)"
                            :course-id="certificateCourseId(item)"
                            :issuing="issuingCourseId === certificateCourseId(item)"
                            @view="viewCertificate(certificateCourseId(item))"
                            @generate="generateCertificate(item)"
                            @continue="continueCourse(item)"
                        />
                    </li>
                </ul>

                <footer v-if="filteredCertificates.length" class="certs-pager">
                    <p>
                        {{
                            $t(
                                filteredCertificates.length === 1
                                    ? 'student.certificates.showingOne'
                                    : 'student.certificates.showingMany',
                                pageRange,
                            )
                        }}
                    </p>
                    <div class="certs-pager__controls">
                        <label>
                            {{ $t('student.certificates.perPage') }}
                            <select
                                :value="itemsPerPage"
                                :aria-label="$t('student.certificates.perPage')"
                                @change="changePageSize"
                            >
                                <option v-for="size in pageSizeOptions" :key="size" :value="size">
                                    {{ size }}
                                </option>
                            </select>
                        </label>
                        <button
                            type="button"
                            :aria-label="$t('student.certificates.previousPage')"
                            :disabled="currentPage <= 1"
                            @click="currentPage -= 1"
                        >
                            <v-icon size="18">mdi-chevron-left</v-icon>
                        </button>
                        <span :aria-label="$t('student.certificates.pageNumber', { n: currentPage })">
                            {{ currentPage }}
                        </span>
                        <button
                            type="button"
                            :aria-label="$t('student.certificates.nextPage')"
                            :disabled="currentPage >= totalPages"
                            @click="currentPage += 1"
                        >
                            <v-icon size="18">mdi-chevron-right</v-icon>
                        </button>
                    </div>
                </footer>
            </section>
        </div>
    </div>
</template>

<script>
    import { mapActions, mapState } from 'vuex';
    import { toast } from 'vue3-toastify';
    import apiCertificates, {
        isMissingCertificateTemplate,
        isTruthyFlag,
        pendingItemLabel,
    } from '@/utils/portal/certificate.js';
    import apiCourse from '@/utils/portal/course.js';
    import { isPortalToken, moduleTokenFromEnrol } from '@/utils/portal/courseToken';
    import CertificateSummary from '@/components/Student/Certificates/CertificateSummary.vue';
    import CertificateFilters from '@/components/Student/Certificates/CertificateFilters.vue';
    import CertificateCard from '@/components/Student/Certificates/CertificateCard.vue';
    import CertificateEmptyState from '@/components/Student/Certificates/CertificateEmptyState.vue';
    import CertificateSkeleton from '@/components/Student/Certificates/CertificateSkeleton.vue';

    export default {
        name: 'StudentCertificates',
        components: {
            CertificateSummary,
            CertificateFilters,
            CertificateCard,
            CertificateEmptyState,
            CertificateSkeleton,
        },
        data() {
            return {
                isLoading: false,
                loadFailed: false,
                noCertificateModel: false,
                issuingCourseId: null,
                hasReady: false,
                dataCertificates: [],
                itemsPerPage: 4,
                pageSizeOptions: [4, 8, 12],
                currentPage: 1,
                filters: {
                    query: '',
                    period: 'all',
                    status: 'all',
                },
            };
        },
        computed: {
            ...mapState(['token', 'user', 'dark']),
            localizedPeriodFilterOptions() {
                return [
                    { label: this.$t('student.filters.allPeriod'), value: 'all' },
                    { label: this.$t('student.filters.last7Days'), value: 7 },
                    { label: this.$t('student.filters.last30Days'), value: 30 },
                    { label: this.$t('student.filters.last90Days'), value: 90 },
                ];
            },
            statusFilterOptions() {
                return [
                    { label: this.$t('student.certificates.statusAll'), value: 'all' },
                    { label: this.$t('student.certificates.issued'), value: 'issued' },
                    { label: this.$t('student.certificates.pending'), value: 'pending' },
                ];
            },
            hasActiveFilters() {
                return (
                    this.filters.query.trim() !== '' ||
                    this.filters.period !== 'all' ||
                    this.filters.status !== 'all'
                );
            },
            filteredCertificates() {
                const q = this.filters.query.trim().toLowerCase();
                const period = this.filters.period;
                const status = this.filters.status;
                const now = new Date();

                return this.listCertificates.filter((item) => {
                    const byQuery =
                        !q ||
                        String(item.courseName || '')
                            .toLowerCase()
                            .includes(q);

                    let byPeriod = true;
                    if (period !== 'all' && item.dateCertificate) {
                        const dt = new Date(item.dateCertificate);
                        if (Number.isNaN(dt.getTime())) {
                            byPeriod = false;
                        } else {
                            const diffMs = now.getTime() - dt.getTime();
                            const diffDays = diffMs / (1000 * 60 * 60 * 24);
                            byPeriod = diffDays <= Number(period);
                        }
                    }

                    const issued = this.isCertificateIssued(item);
                    const byStatus =
                        status === 'all' ||
                        (status === 'issued' && issued) ||
                        (status === 'pending' && !issued);

                    return byQuery && byPeriod && byStatus;
                });
            },
            listCertificates() {
                return Array.isArray(this.dataCertificates) ? this.dataCertificates : [];
            },
            summary() {
                const issued = this.listCertificates.filter((item) => this.isCertificateIssued(item)).length;
                return {
                    total: this.listCertificates.length,
                    issued,
                    pending: this.listCertificates.length - issued,
                };
            },
            totalPages() {
                return Math.max(1, Math.ceil(this.filteredCertificates.length / this.itemsPerPage));
            },
            pagedCertificates() {
                const start = (this.currentPage - 1) * this.itemsPerPage;
                return this.filteredCertificates.slice(start, start + this.itemsPerPage);
            },
            pageRange() {
                const total = this.filteredCertificates.length;
                if (!total) return { from: 0, to: 0, total: 0 };
                const from = (this.currentPage - 1) * this.itemsPerPage + 1;
                const to = Math.min(this.currentPage * this.itemsPerPage, total);
                return { from, to, total };
            },
        },
        watch: {
            filteredCertificates() {
                if (this.currentPage > this.totalPages) {
                    this.currentPage = 1;
                }
            },
            'filters.query'() {
                this.currentPage = 1;
            },
            'filters.period'() {
                this.currentPage = 1;
            },
            'filters.status'() {
                this.currentPage = 1;
            },
        },
        created() {
            this.loadCertificates();
        },
        methods: {
            ...mapActions(['storeCurrentCoursePortal']),
            async loadCertificates() {
                try {
                    this.isLoading = true;
                    this.loadFailed = false;
                    this.noCertificateModel = false;
                    const certificates =
                        await apiCertificates.getPortalCertificates(this.token);
                    this.dataCertificates = certificates;
                } catch (error) {
                    if (isMissingCertificateTemplate(error)) {
                        this.dataCertificates = [];
                        this.noCertificateModel = true;
                        return;
                    }
                    this.loadFailed = true;
                    toast.error(this.$t('student.certificates.toastLoadError'));
                    throw error;
                } finally {
                    this.isLoading = false;
                    this.hasReady = true;
                }
            },
            certificateRow(item) {
                if (item?.raw && (item.raw.courseId != null || item.raw.courseName)) {
                    return item.raw;
                }
                return item;
            },
            certificateCourseId(item) {
                const row = this.certificateRow(item);
                return row?.courseId ?? row?.id;
            },
            isCertificateIssued(item) {
                const row = this.certificateRow(item);
                return isTruthyFlag(row?.issued);
            },
            isCertificateEligible(item) {
                const row = this.certificateRow(item);
                return isTruthyFlag(row?.eligible) && !this.isCertificateIssued(row);
            },
            viewCertificate(courseId) {
                if (courseId == null || courseId === '') return;
                this.$router.push(`/aluno/certificado/${courseId}`);
            },
            async continueCourse(item) {
                const row = item && (item.courseName || item.courseId || item.raw)
                    ? item
                    : null;
                const source = row ? this.certificateRow(row) : { courseId: item };
                const token = await this.resolvePlayerToken(source);
                if (!isPortalToken(token)) {
                    toast.error(this.$t('student.player.modulesLoadError'));
                    return;
                }
                window.location.href = `/aluno/curso/${encodeURIComponent(token)}`;
            },
            async resolvePlayerToken(row) {
                const direct = moduleTokenFromEnrol(row, this.user?.type)
                    || (isPortalToken(row?.courseToken) ? row.courseToken : null)
                    || (isPortalToken(row?.enrolToken) ? row.enrolToken : null);
                if (direct) return direct;

                const name = String(row?.courseName || '').trim().toLowerCase();
                if (!name || !this.token) return null;
                try {
                    const catalog = await apiCourse.getDataCourses(this.token, {
                        search: row.courseName,
                        size: 50,
                    });
                    const match = (catalog?.courses || []).find((entry) => {
                        const label = String(
                            entry?.course?.name || entry?.course?.comercialName || '',
                        ).trim().toLowerCase();
                        return label === name;
                    });
                    if (!match) return null;
                    this.storeCurrentCoursePortal({ currentCoursePortal: match });
                    return moduleTokenFromEnrol(match, this.user?.type);
                } catch (error) {
                    console.error('Erro ao resolver hash do curso:', error);
                    return null;
                }
            },
            async generateCertificate(item) {
                const courseId = this.certificateCourseId(item);
                if (courseId == null || this.issuingCourseId != null) return;
                this.issuingCourseId = courseId;
                try {
                    await apiCertificates.issueCertificate(this.token, courseId);
                    this.$router.push(`/aluno/certificado/${courseId}`);
                } catch (error) {
                    toast.error(this.$t('student.certificates.issueError'));
                } finally {
                    this.issuingCourseId = null;
                }
            },
            pendingLabels(item) {
                const row = this.certificateRow(item);
                const lessons = Array.isArray(row?.pendingLessons) ? row.pendingLessons : [];
                const quizzes = Array.isArray(row?.pendingQuizzes) ? row.pendingQuizzes : [];
                return [...lessons, ...quizzes]
                    .map((entry) => pendingItemLabel(entry))
                    .filter((label) => label);
            },
            formatDate(date) {
                if (!date) return this.$t('student.dates.unavailable');
                try {
                    const dateObj = new Date(date);
                    if (Number.isNaN(dateObj.getTime())) {
                        return this.$t('student.dates.invalid');
                    }
                    return dateObj.toLocaleDateString(this.$i18n?.locale || 'pt-BR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                    });
                } catch (error) {
                    return this.$t('student.dates.invalid');
                }
            },
            statusDate(item) {
                if (this.isCertificateIssued(item) && item.dateCertificate) {
                    const formatted = this.formatDate(item.dateCertificate);
                    if (formatted === this.$t('student.dates.invalid')) return formatted;
                    return this.$t('student.certificates.completedOn', { date: formatted });
                }
                return this.$t('student.dates.unavailable');
            },
            formatDuration(seconds) {
                const sec = Number(seconds);
                if (!sec || Number.isNaN(sec) || sec <= 0) return '';
                const hours = Math.floor(sec / 3600);
                const minutes = Math.floor((sec % 3600) / 60);
                if (hours === 1 && minutes === 0) {
                    return this.$t('student.certificates.durationHourOne');
                }
                if (hours > 0 && minutes === 0) {
                    return this.$t('student.certificates.durationHours', { n: hours });
                }
                if (hours > 0) {
                    return this.$t('student.certificates.durationHoursMinutes', { hours, minutes });
                }
                return this.$t('student.certificates.durationMinutes', { n: minutes });
            },
            clearFilters() {
                this.filters.query = '';
                this.filters.period = 'all';
                this.filters.status = 'all';
            },
            changePageSize(event) {
                this.itemsPerPage = Number(event.target.value) || 10;
                this.currentPage = 1;
            },
        },
    };
</script>

<style>
    .studentPortal:has(.student-portal-certificates) {
        height: 100dvh;
        max-height: 100dvh;
        overflow: hidden;
    }

    .studentPortal:has(.student-portal-certificates) > .global_header,
    .studentPortal:has(.student-portal-certificates) > .global_footer {
        flex-shrink: 0;
    }

    .studentPortal:has(.student-portal-certificates) .student-portal-main-content {
        display: flex;
        flex: 1 1 auto;
        flex-direction: column;
        min-height: 0;
        overflow: hidden;
    }

    .studentPortal:has(.student-portal-certificates) .student-portal-main-content > div {
        display: flex;
        flex: 1 1 auto;
        flex-direction: column;
        height: 100%;
        min-height: 0;
        overflow: hidden;
    }
</style>

<style scoped>
    .student-portal-certificates {
        --certs-bg: #f5f7fa;
        --certs-surface: #ffffff;
        --certs-text: #111827;
        --certs-muted: #6b7280;
        --certs-line: #e8eaf0;
        --certs-accent: #1ac4cd;
        --certs-refresh: #0f766e;
        --certs-card-hover: #d5d8e2;
        --certs-kpi-accent: #f0fbfb;
        --certs-kpi-done: #f3f1fb;
        --certs-kpi-pending: #fff7ed;
        --certs-kpi-text: #111827;
        --certs-kpi-muted: #6b7280;
        --certs-kpi-icon-accent-bg: rgba(26, 196, 205, 0.16);
        --certs-kpi-icon-accent: #0f766e;
        --certs-kpi-icon-done-bg: rgba(109, 90, 205, 0.14);
        --certs-kpi-icon-done: #5b4db8;
        --certs-kpi-icon-pending-bg: rgba(217, 119, 6, 0.14);
        --certs-kpi-icon-pending: #b45309;
        --certs-badge-pending-bg: #f3f4f6;
        --certs-badge-pending: #4b5563;
        --certs-badge-issued-bg: #ecfdf3;
        --certs-badge-issued: #047857;
        --certs-pending-text: #6b7280;
        --certs-pending-line: #e6e8ee;
        display: flex;
        flex: 1 1 auto;
        flex-direction: column;
        height: 100%;
        min-height: 0;
        overflow: hidden;
        background: var(--certs-bg);
        color: var(--certs-text);
    }

    .student-portal-certificates.dark {
        --certs-bg: #0f0e12;
        --certs-surface: #1a191f;
        --certs-text: #f3f4f6;
        --certs-muted: #9ca3af;
        --certs-line: rgba(255, 255, 255, 0.08);
        --certs-refresh: #5eead4;
        --certs-card-hover: rgba(255, 255, 255, 0.16);
        --certs-kpi-accent: rgba(26, 196, 205, 0.14);
        --certs-kpi-done: rgba(167, 139, 250, 0.14);
        --certs-kpi-pending: rgba(251, 191, 36, 0.14);
        --certs-kpi-text: #f3f4f6;
        --certs-kpi-muted: #c4c9d4;
        --certs-kpi-icon-accent-bg: rgba(26, 196, 205, 0.2);
        --certs-kpi-icon-accent: #5eead4;
        --certs-kpi-icon-done-bg: rgba(167, 139, 250, 0.2);
        --certs-kpi-icon-done: #ddd6fe;
        --certs-kpi-icon-pending-bg: rgba(251, 191, 36, 0.18);
        --certs-kpi-icon-pending: #fcd34d;
        --certs-badge-pending-bg: rgba(148, 163, 184, 0.16);
        --certs-badge-pending: #e2e8f0;
        --certs-badge-issued-bg: rgba(16, 185, 129, 0.16);
        --certs-badge-issued: #6ee7b7;
        --certs-pending-text: #c4c9d4;
        --certs-pending-line: rgba(255, 255, 255, 0.08);
    }

    .student-portal-certificates__inner {
        display: flex;
        flex: 1 1 auto;
        flex-direction: column;
        width: 100%;
        max-width: 1600px;
        height: 100%;
        min-height: 0;
        margin: 0 auto;
        padding: 12px 20px;
        overflow: hidden;
        box-sizing: border-box;
    }

    .certs-hero,
    .certs-panel {
        background: var(--certs-surface);
        border: 1px solid var(--certs-line);
        border-radius: 16px;
        box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
    }

    .certs-hero {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 24px;
        flex-shrink: 0;
        padding: 16px 22px;
        margin-bottom: 12px;
    }

    .certs-hero__copy {
        min-width: 0;
        flex: 1 1 auto;
    }

    .certs-hero__title-row {
        display: flex;
        align-items: center;
        gap: 12px;
        flex-wrap: wrap;
    }

    .certs-hero h1 {
        margin: 0;
        font-size: 1.65rem;
        font-weight: 700;
        letter-spacing: -0.02em;
        color: var(--certs-text);
    }

    .certs-hero__lead {
        margin: 6px 0 0;
        max-width: 34rem;
        color: var(--certs-muted);
        font-size: 0.9rem;
        line-height: 1.45;
    }

    .certs-hero__refresh {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        height: 28px;
        padding: 0 2px;
        border: 0;
        background: transparent;
        color: var(--certs-refresh);
        font-size: 0.82rem;
        font-weight: 700;
        cursor: pointer;
    }

    .certs-hero__refresh:disabled {
        opacity: 0.6;
        cursor: wait;
    }

    .certs-hero__refresh:focus-visible {
        outline: 2px solid var(--certs-accent);
        outline-offset: 3px;
    }

    .certs-panel {
        display: flex;
        flex: 1 1 auto;
        flex-direction: column;
        min-height: 0;
        padding: 14px;
        overflow: hidden;
    }

    .certs-panel > :deep(.cert-filters) {
        flex-shrink: 0;
    }

    .certs-list {
        display: flex;
        flex: 1 1 auto;
        flex-direction: column;
        justify-content: flex-start;
        gap: 12px;
        min-height: 0;
        margin: 14px 0 0;
        padding: 0;
        list-style: none;
        overflow: hidden;
    }

    .certs-list > li {
        flex-shrink: 0;
    }

    .certs-pager {
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-shrink: 0;
        gap: 12px;
        margin-top: 16px;
        padding-top: 14px;
        border-top: 1px solid var(--certs-line);
        color: var(--certs-muted);
        font-size: 0.88rem;
    }

    .certs-pager p {
        margin: 0;
    }

    .certs-pager__controls {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .certs-pager__controls label {
        display: inline-flex;
        align-items: center;
        gap: 8px;
    }

    .certs-pager__controls select,
    .certs-pager__controls button {
        height: 36px;
        border: 1px solid var(--certs-line);
        border-radius: 8px;
        background: var(--certs-surface);
        color: var(--certs-text);
    }

    .certs-pager__controls select {
        padding: 0 8px;
        cursor: pointer;
    }

    .certs-pager__controls button {
        width: 36px;
        display: grid;
        place-items: center;
        cursor: pointer;
    }

    .certs-pager__controls button:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }

    .certs-pager__controls button:focus-visible,
    .certs-pager__controls select:focus-visible {
        outline: 2px solid var(--certs-accent);
        outline-offset: 2px;
    }

    .certs-pager__controls span {
        min-width: 28px;
        text-align: center;
        font-weight: 700;
        color: var(--certs-text);
    }

    @media (max-width: 900px) {
        .certs-hero {
            flex-direction: column;
            align-items: stretch;
        }

        .certs-pager {
            flex-direction: column;
            align-items: flex-start;
        }
    }
</style>
